import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  AttachFile as AttachFileIcon,
  GetApp as DownloadIcon,
  LinkedIn as LinkedInIcon,
  TextFields as TextFormatIcon,
} from '@mui/icons-material';
import { DailyReportSimple, DailyReportAdvanced, Candidate, Position, Application } from '../../types';
import airtableService from '../../services/airtable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DailyReporting: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setCandidates] = useState<Candidate[]>([]);
  const [, setPositions] = useState<Position[]>([]);
  const [, setApplications] = useState<Application[]>([]);

  const [simpleReports, setSimpleReports] = useState<DailyReportSimple[]>([]);
  const [advancedReports, setAdvancedReports] = useState<DailyReportAdvanced[]>([]);
  const [advancedSortBy, setAdvancedSortBy] = useState<'position' | 'application'>('position');
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);

  // Helper function to get full candidate name from simple reports
  const getFullCandidateName = (report: DailyReportSimple): string => {
    // If we have both candidateName (first name) and candidateLastName (lookup), combine them
    if (report.candidateName && report.candidateLastName) {
      return `${report.candidateName} ${report.candidateLastName}`;
    }
    // If we have linked candidate data, use that
    if (report.candidate?.firstName && report.candidate?.lastName) {
      return `${report.candidate.firstName} ${report.candidate.lastName}`;
    }
    // Fallback to just candidateName if it contains full name already
    return report.candidateName || 'Candidate Name';
  };

  // Helper function to get role title from simple reports
  const getRoleTitle = (report: DailyReportSimple): string => {
    // Use the lookup field if available, otherwise fallback to the linked record ID
    return report.roleTitleLookup || report.roleTitle || 'Unknown Role';
  };

  // Parse step number from string or number - identical logic to CandidateUpdates.tsx
  const parseStepNumber = (currentStep: string | number): number => {
    if (typeof currentStep === 'number') {
      return Math.max(1, Math.min(20, currentStep));
    }

    if (typeof currentStep === 'string') {
      // Extract number from text like "Step 12" -> 12
      const match = currentStep.match(/Step\s*(\d+)/i);
      if (match && match[1]) {
        const stepNumber = parseInt(match[1], 10);
        return Math.max(1, Math.min(20, stepNumber));
      }
    }

    return 1; // Default fallback
  };

  // Get current interview step for a specific candidate - SAME logic as CandidateUpdates
  const getCurrentStepForCandidate = (candidateId: string, timelineEvents: any[]): number => {
    if (!candidateId) return 1;

    // Get ALL timeline events that might be related to this candidate
    const allRelatedEvents = timelineEvents.filter(event => {
      const matchDirectCandidate = event.candidate?.id === candidateId;
      const matchApplicationCandidate = event.application?.candidateId === candidateId;
      const matchApplicationId = event.applicationId && event.application?.candidateId === candidateId;

      return matchDirectCandidate || matchApplicationCandidate || matchApplicationId;
    });

    if (allRelatedEvents.length === 0) {
      return 1;
    }

    // Find events that have a currentStep value and parse the step number
    const eventsWithSteps = allRelatedEvents
      .filter(event => event.currentStep != null && event.currentStep !== undefined)
      .map(event => ({
        ...event,
        parsedStepNumber: parseStepNumber(event.currentStep!)
      }))
      .filter(event => event.parsedStepNumber > 0)
      .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());

    if (eventsWithSteps.length > 0) {
      return eventsWithSteps[0].parsedStepNumber;
    }

    return 1;
  };


  // Remove the generateAdvancedReports function entirely - we'll use real data from Airtable

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching real data from Airtable tables...');

      // Fetch all data we need for linking
      const [
        candidatesResponse,
        positionsResponse,
        applicationsResponse,
        timelineEventsResponse,
        simpleReportsResponse,
        advancedReportsResponse
      ] = await Promise.all([
        airtableService.getCandidates(),
        airtableService.getPositions(),
        airtableService.getApplications(),
        airtableService.getTimelineEvents({ limit: 50, page: 1 }),
        airtableService.getDailyReportsSimple(),
        airtableService.getDailyReportsAdvanced()
      ]);

      console.log('📊 API Responses:', {
        candidates: candidatesResponse.success ? candidatesResponse.data.length : 'FAILED',
        positions: positionsResponse.success ? positionsResponse.data.length : 'FAILED',
        applications: applicationsResponse.success ? applicationsResponse.data.length : 'FAILED',
        simpleReports: simpleReportsResponse.success ? simpleReportsResponse.data.length : 'FAILED',
        advancedReports: advancedReportsResponse.success ? advancedReportsResponse.data.length : 'FAILED'
      });

      // Store the base data
      const candidates = candidatesResponse.success ? candidatesResponse.data : [];
      const positions = positionsResponse.success ? positionsResponse.data : [];
      const applications = applicationsResponse.success ? applicationsResponse.data : [];

      setCandidates(candidates);
      setPositions(positions);
      setApplications(applications);

      // Link timeline events to related data (same as CandidateUpdates)
      if (timelineEventsResponse.success) {
        const linkedTimelineEvents = timelineEventsResponse.data
          .map(event => {
            const application = applications.find(a => a.id === event.applicationId);
            const candidate = candidates.find(c => c.id === application?.candidateId);
            const position = positions.find(p => p.id === application?.positionId);

            return {
              ...event,
              application,
              candidate,
              position
            };
          })
          .filter(event => event.candidate !== undefined); // Only show events with visible candidates

        setTimelineEvents(linkedTimelineEvents);
      }

      // Load Simple Reports - use real data and link candidate data
      if (simpleReportsResponse.success && simpleReportsResponse.data.length > 0) {
        console.log('✅ Using real Simple Reports from Airtable');

        // Link candidate data to simple reports by matching candidate name or ID
        const linkedSimpleReports = simpleReportsResponse.data
          .map(report => {
            // Try to find candidate by ID if available, otherwise by name matching
            const candidate = candidates.find(c =>
              c.id === report.candidateId ||
              `${c.firstName} ${c.lastName}` === report.candidateName ||
              (report.candidateName.includes(c.firstName) && report.candidateName.includes(c.lastName))
            );

            return { ...report, candidate };
          })
          .filter(report => report.candidate !== undefined); // Only show reports with visible candidates

        console.log('🔍 Simple Reports Debug:', {
          total: linkedSimpleReports.length,
          sampleData: linkedSimpleReports.slice(0, 2).map(r => ({
            id: r.id,
            candidateName: r.candidateName,
            currentStepFromTimeline: r.currentStepFromTimeline,
            currentStepType: typeof r.currentStepFromTimeline
          }))
        });

        setSimpleReports(linkedSimpleReports);
      } else {
        console.log('⚠️ No Simple Reports found, generating from applications data');
        generateFallbackSimpleReports();
      }

      // Load Advanced Reports - use ONLY real data from Daily Reports - Advanced table
      if (advancedReportsResponse.success && advancedReportsResponse.data.length > 0) {
        console.log('✅ Using real Advanced Reports from Airtable');

        // Link the real data properly
        const linkedAdvancedReports = advancedReportsResponse.data
          .map(report => {
            // Find linked candidate and position data
            const candidate = candidates.find(c => c.id === report.candidateId);
            const position = positions.find(p => p.id === report.positionId);
            const application = applications.find(a => a.id === report.applicationId);

            console.log(`🔗 Linking report ${report.id}:`, {
              candidateFound: candidate ? `${candidate.firstName} ${candidate.lastName}` : 'NOT FOUND',
              positionFound: position ? position.title : 'NOT FOUND',
              applicationFound: application ? application.applicationStatus : 'NOT FOUND'
            });

            return {
              ...report,
              candidate,
              position,
              application
            };
          })
          .filter(report => report.candidate !== undefined); // Only show reports with visible candidates

        console.log('🔍 Advanced Reports Debug:', {
          total: linkedAdvancedReports.length,
          sampleData: linkedAdvancedReports.slice(0, 2).map(r => ({
            id: r.id,
            currentStepFromTimeline: r.currentStepFromTimeline,
            currentStepType: typeof r.currentStepFromTimeline
          }))
        });

        setAdvancedReports(linkedAdvancedReports);
      } else {
        console.log('⚠️ No Advanced Reports found in Airtable table');
        setAdvancedReports([]);
      }

    } catch (err) {
      console.error('❌ Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateFallbackSimpleReports = () => {
    const fallbackReports: DailyReportSimple[] = [
      {
        id: 'DR-2024-001',
        reportDate: new Date(),
        roleTitle: 'recWYV3dJMVqWVtGz',
        roleTitleLookup: 'Managing Director',
        candidateName: 'Joshua Tecchio',
        location: 'NY',
        practiceArea: 'Assessment',
        interviewSummary: 'Met Erin (8/22), Sean (9/9, positive), Pascal (9/24).',
        nextActions: 'Sean Dinea to gather feedback from Jeremy at AlixPartners. Await Pascal feedback. Misty to gather compensation information and expectations, timings etc.',
        status: 'Active',
        createdBy: 'scott@m4consulting.net',
        createdDate: new Date(),
      },
      {
        id: 'DR-2024-002',
        reportDate: new Date(),
        roleTitle: 'recWYV3dJMVqWVtGz',
        roleTitleLookup: 'Managing Director',
        candidateName: 'Silvia Christma',
        location: 'NY',
        practiceArea: 'Assessment',
        interviewSummary: 'Met Pascal (8/27), Sean (9/2), Erin (9/17) – all positive.',
        nextActions: 'Justus – new meeting times presented to Silvia, pending response',
        status: 'Pending',
        createdBy: 'misty@m4consulting.net',
        createdDate: new Date(),
      },
      {
        id: 'DR-2024-003',
        reportDate: new Date(),
        roleTitle: 'recABC123XYZ789',
        roleTitleLookup: 'Data & Innovation',
        candidateName: 'Ryan Heinler',
        location: 'Georgia',
        practiceArea: 'Technology',
        interviewSummary: 'Met with Erin, Sean Dineen, and Katherine; meeting with Harpreet next.',
        nextActions: 'Getting close to a potential offer. Katherine and Erin are managing the process.',
        status: 'Offer Stage',
        createdBy: 'scott@onelaw.us',
        createdDate: new Date(),
      },
    ];
    setSimpleReports(fallbackReports);
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'primary';
      case 'Hold': return 'warning';
      case 'Pending': return 'info';
      case 'Pending Feedback': return 'info';
      case 'Offer Stage': return 'success';
      case 'Ready for Offer': return 'success';
      case 'Offer Extended': return 'secondary';
      case 'Hired': return 'success';
      case 'Rejected': return 'error';
      case 'Withdrawn': return 'default';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const generateSimpleReportsText = () => {
    const groupedReports = groupReportsByRole(simpleReports);
    let textContent = `DAILY REPORTS - SIMPLE\n`;
    textContent += `Report Date: ${reportDate}\n`;
    textContent += `Generated: ${new Date().toLocaleDateString()}\n`;
    textContent += `\n${'='.repeat(60)}\n\n`;

    Object.entries(groupedReports).forEach(([roleTitle, reports]) => {
      textContent += `ROLE: ${roleTitle.toUpperCase()}\n`;
      textContent += `${'-'.repeat(40)}\n\n`;

      reports.forEach((report, index) => {
        textContent += `${index + 1}. ${getFullCandidateName(report)}, ${report.location}`;
        
        if (report.practiceArea) textContent += `, ${report.practiceArea}`;
        textContent += `\n`;
        // textContent += `Status: ${report.status}\n`;

        // Add candidate links if available
        if (report.candidate?.linkedinProfile) {
          textContent += `${report.candidate.linkedinProfile}\n`;
        }

        textContent += `Completed Interviews: ${report.interviewSummary}\n`;
        if (report.nextActions) {
          textContent += `Next Actions: ${report.nextActions}\n`;
        }
        textContent += `\n`;
      });
      textContent += `\n`;
    });

    return textContent;
  };

  const generateAdvancedReportsText = () => {
    let textContent = `DAILY REPORTS - ADVANCED FORMAT\n`;
    textContent += `Report Date: ${reportDate}\n`;
    textContent += `Generated: ${new Date().toLocaleDateString()}\n`;
    textContent += `\n${'='.repeat(60)}\n\n`;

    advancedReports.forEach((report, index) => {
      textContent += `REPORT ${index + 1}\n`;
      textContent += `${'-'.repeat(20)}\n`;

      const candidateName = report.candidate?.firstName && report.candidate?.lastName
        ? `${report.candidate.firstName} ${report.candidate.lastName}`
        : report.candidate?.firstName || report.candidate?.lastName || 'Candidate Name';

      textContent += `Candidate: ${candidateName}\n`;

      // Add candidate links if available
      if (report.candidate?.linkedinProfile) {
        textContent += `${report.candidate.linkedinProfile}\n`;
      }

      const positionTitle = report.position?.title;
      const companyName = report.candidate?.currentCompany || report.position?.company?.name;
      if (positionTitle || companyName) {
        textContent += `Position: ${positionTitle || 'Position TBD'}`;
        if (companyName) textContent += ` at ${companyName}`;
        textContent += `\n`;
      }

      textContent += `Status: ${report.status}\n`;
      textContent += `Priority: ${report.priorityLevel}\n`;

      if (report.candidateRating) {
        textContent += `Rating: ${report.candidateRating}\n`;
      }

      textContent += `\nCompleted Interviews:\n${report.interviewSummary}\n\n`;

      textContent += `Next Steps:\n${report.nextSteps}\n\n`;

      if (report.salaryExpectation) {
        textContent += `Salary Expectation: $${report.salaryExpectation.toLocaleString()}\n`;
      }

      if (report.scheduledFollowup) {
        textContent += `Follow-up Scheduled: ${report.scheduledFollowup.toLocaleDateString()}\n`;
      }

      textContent += `M4 Consultant: ${report.m4Consultant}\n`;

      if (report.clientContact) {
        textContent += `Client Contact: ${report.clientContact}\n`;
      }

      if (report.additionalNotes) {
        textContent += `\nAdditional Notes:\n${report.additionalNotes}\n`;
      }

      textContent += `\n${'='.repeat(40)}\n\n`;
    });

    return textContent;
  };

  const handleTextFormat = () => {
    const currentTabReports = tabValue === 0 ? 'simple' : 'advanced';
    const textContent = currentTabReports === 'simple'
      ? generateSimpleReportsText()
      : generateAdvancedReportsText();

    // Create a new window with the plain text content
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Daily Reports - Basic Text Format</title>
            <style>
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.4;
                padding: 20px;
                background: white;
                color: black;
                max-width: 800px;
              }
              pre {
                white-space: pre-wrap;
                word-wrap: break-word;
              }
            </style>
          </head>
          <body>
            <pre>${textContent}</pre>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const groupReportsByRole = (reports: DailyReportSimple[]) => {
    const grouped: { [key: string]: DailyReportSimple[] } = {};
    reports.forEach(report => {
      const roleTitle = getRoleTitle(report);
      if (!grouped[roleTitle]) {
        grouped[roleTitle] = [];
      }
      grouped[roleTitle].push(report);
    });
    return grouped;
  };

  const groupAdvancedReportsByPosition = (reports: DailyReportAdvanced[]) => {
    const grouped: { [key: string]: DailyReportAdvanced[] } = {};
    reports.forEach(report => {
      const positionTitle = report.position?.title || 'Unknown Position';
      if (!grouped[positionTitle]) {
        grouped[positionTitle] = [];
      }
      grouped[positionTitle].push(report);
    });
    return grouped;
  };

  const groupAdvancedReportsByApplication = (reports: DailyReportAdvanced[]) => {
    const grouped: { [key: string]: DailyReportAdvanced[] } = {};
    reports.forEach(report => {
      const applicationStatus = report.application?.applicationStatus || 'Unknown Status';
      if (!grouped[applicationStatus]) {
        grouped[applicationStatus] = [];
      }
      grouped[applicationStatus].push(report);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
        <Box display="flex" alignItems="center" gap={2}>
          <TodayIcon sx={{ color: '#60a5fa', fontSize: '2rem' }} />
          <Typography
            variant="h4"
            className="title-gradient"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Daily Reporting
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            size="small"
            label="Report Date"
            InputLabelProps={{ shrink: true }}
          />
          {tabValue === 1 && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Group By</InputLabel>
              <Select
                value={advancedSortBy}
                label="Group By"
                onChange={(e) => setAdvancedSortBy(e.target.value as 'position' | 'application')}
              >
                <MenuItem value="position">Position</MenuItem>
                <MenuItem value="application">Application</MenuItem>
              </Select>
            </FormControl>
          )}
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<TextFormatIcon />}
            onClick={handleTextFormat}
            sx={{
              borderColor: 'rgba(96, 165, 250, 0.5)',
              color: '#60a5fa',
              '&:hover': {
                borderColor: '#60a5fa',
                backgroundColor: 'rgba(96, 165, 250, 0.1)'
              }
            }}
          >
            Basic Text Format
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Report Header for Print */}
      <Box className="print-only" sx={{ mb: 4, textAlign: 'center', display: 'none' }}>
        <Typography variant="h3" gutterBottom>
          M4 Consulting ATS
        </Typography>
        <Typography variant="h4" gutterBottom>
          Daily Report - {new Date(reportDate).toLocaleDateString()}
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="no-print">
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Simple Report" />
          <Tab label="Advanced Report" />
        </Tabs>
      </Box>

      {/* Simple Report Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box>
          {Object.entries(groupReportsByRole(simpleReports)).map(([roleTitle, reports]) => (
            <Box key={roleTitle} mb={4}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  borderBottom: '2px solid',
                  borderColor: '#60a5fa',
                  pb: 1,
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                ROLE: {roleTitle}
              </Typography>

              <Grid container spacing={3}>
                {reports.map((report) => (
                  <Grid item xs={12} sm={6} md={4} key={report.id}>
                    <Card sx={{ mb: 2, boxShadow: 1, height: '100%' }}>
                      <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box display="flex" gap={2} alignItems="flex-start" flex={1}>
                        {/* Candidate Image */}
                        {report.candidate?.candidateImage && report.candidate.candidateImage.length > 0 && (
                          <Box
                            component="img"
                            src={report.candidate.candidateImage[0].url}
                            alt={getFullCandidateName(report)}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 2,
                              objectFit: 'cover',
                              border: '2px solid rgba(96, 165, 250, 0.3)',
                              flexShrink: 0
                            }}
                          />
                        )}

                        <Box flex={1}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 'bold',
                              color: '#ffffff',
                              mb: 1
                            }}
                          >
                            {getFullCandidateName(report)}, {report.location}
                            {report.practiceArea && `, ${report.practiceArea}`}
                          </Typography>

                          {/* Interview Progress Bar */}
                          {(() => {
                            const currentStep = getCurrentStepForCandidate(report.candidateId || '', timelineEvents);
                            return (
                              <Box sx={{ mb: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                                    Interview Progress
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                                    Step {currentStep}/20
                                  </Typography>
                                </Box>
                                <Box sx={{ position: 'relative' }}>
                                  <Box
                                    sx={{
                                      height: 4,
                                      background: 'linear-gradient(90deg, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)',
                                      borderRadius: 2,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${(currentStep / 20) * 100}%`,
                                        background: currentStep >= 20
                                          ? 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'
                                          : 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                                        borderRadius: 2,
                                        transition: 'width 0.3s ease'
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })()}

                          {/* Resume Attachment & LinkedIn Profile */}
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {report.candidate?.resumeAttachment && report.candidate.resumeAttachment.length > 0 && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <AttachFileIcon sx={{ fontSize: 14, color: '#60a5fa' }} />
                                <Typography
                                  component="a"
                                  href={report.candidate.resumeAttachment[0].url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  sx={{
                                    color: '#60a5fa',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  {report.candidate.resumeAttachment[0].filename || 'Resume'}
                                  <DownloadIcon sx={{ fontSize: 12 }} />
                                </Typography>
                              </Box>
                            )}

                            {report.candidate?.linkedinProfile && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LinkedInIcon sx={{ fontSize: 14, color: '#0A66C2' }} />
                                <Typography
                                  component="a"
                                  href={report.candidate.linkedinProfile}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  sx={{
                                    color: '#0A66C2',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  LinkedIn Profile
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Chip
                        label={report.status}
                        color={getStatusColor(report.status) as any}
                        size="small"
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      paragraph
                      sx={{
                        mb: 2,
                        color: 'rgba(255, 255, 255, 0.9)',
                      }}
                    >
                      {report.interviewSummary}
                    </Typography>

                    {report.nextActions && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: 'italic',
                          pl: 2,
                          borderLeft: '3px solid',
                          borderColor: '#60a5fa',
                          color: 'rgba(255, 255, 255, 0.8)',
                        }}
                      >
                        {report.nextActions}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />
            </Box>
          ))}
        </Box>
      </TabPanel>

      {/* Advanced Report Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box>
          {Object.entries(
            advancedSortBy === 'position'
              ? groupAdvancedReportsByPosition(advancedReports)
              : groupAdvancedReportsByApplication(advancedReports)
          ).map(([groupTitle, reports]) => (
            <Box key={groupTitle} mb={4}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  borderBottom: '2px solid',
                  borderColor: '#60a5fa',
                  pb: 1,
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                {advancedSortBy === 'position' ? 'POSITION' : 'APPLICATION STATUS'}: {groupTitle}
              </Typography>

              <Grid container spacing={3}>
                {reports.map((report) => (
                  <Grid item xs={12} md={6} key={report.id}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box display="flex" gap={2} alignItems="flex-start" flex={1}>
                            {/* Candidate Image */}
                            {report.candidate?.candidateImage && report.candidate.candidateImage.length > 0 && (
                              <Box
                                component="img"
                                src={report.candidate.candidateImage[0].url}
                                alt={`${report.candidate.firstName} ${report.candidate.lastName}`}
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 2,
                                  objectFit: 'cover',
                                  border: '2px solid rgba(96, 165, 250, 0.3)',
                                  flexShrink: 0
                                }}
                              />
                            )}

                            <Box flex={1}>
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: '#ffffff', fontWeight: 600 }}
                              >
                                {report.candidate?.firstName && report.candidate?.lastName
                                  ? `${report.candidate.firstName} ${report.candidate.lastName}`
                                  : report.candidate?.firstName || report.candidate?.lastName || 'Candidate Name'}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}
                              >
                                {(() => {
                                  const positionTitle = report.position?.title;
                                  const companyName = report.candidate?.currentCompany || report.position?.company?.name;

                                  if (positionTitle && companyName) {
                                    return `${positionTitle} at ${companyName}`;
                                  } else if (positionTitle) {
                                    return positionTitle;
                                  } else if (companyName) {
                                    return `Position at ${companyName}`;
                                  } else {
                                    return `Application #${report.id}`;
                                  }
                                })()}
                              </Typography>

                              {/* Interview Progress Bar */}
                              {(() => {
                                const currentStep = getCurrentStepForCandidate(report.candidateId || '', timelineEvents);
                                return (
                                  <Box sx={{ mb: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                                        Interview Progress
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem' }}>
                                        Step {currentStep}/20
                                      </Typography>
                                    </Box>
                                    <Box sx={{ position: 'relative' }}>
                                      <Box
                                        sx={{
                                          height: 4,
                                          background: 'linear-gradient(90deg, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)',
                                          borderRadius: 2,
                                          overflow: 'hidden'
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            height: '100%',
                                            width: `${(currentStep / 20) * 100}%`,
                                            background: currentStep >= 20
                                              ? 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'
                                              : 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                                            borderRadius: 2,
                                            transition: 'width 0.3s ease'
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              })()}

                              {/* Resume Attachment & LinkedIn Profile */}
                              <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                                {report.candidate?.resumeAttachment && report.candidate.resumeAttachment.length > 0 && (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <AttachFileIcon sx={{ fontSize: 16, color: '#60a5fa' }} />
                                    <Typography
                                      component="a"
                                      href={report.candidate.resumeAttachment[0].url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="body2"
                                      sx={{
                                        color: '#60a5fa',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                      }}
                                    >
                                      {report.candidate.resumeAttachment[0].filename || 'Resume'}
                                      <DownloadIcon sx={{ fontSize: 14 }} />
                                    </Typography>
                                  </Box>
                                )}

                                {report.candidate?.linkedinProfile && (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <LinkedInIcon sx={{ fontSize: 16, color: '#0A66C2' }} />
                                    <Typography
                                      component="a"
                                      href={report.candidate.linkedinProfile}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="body2"
                                      sx={{
                                        color: '#0A66C2',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      LinkedIn Profile
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>

                          <Box display="flex" flexDirection="column" alignItems="end" gap={1}>
                            <Chip
                              label={report.status}
                              color={getStatusColor(report.status) as any}
                              size="small"
                            />
                            <Chip
                              label={report.priorityLevel}
                              color={report.priorityLevel === 'High' ? 'error' :
                                     report.priorityLevel === 'Medium' ? 'warning' : 'success'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          paragraph
                          sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                        >
                          <strong style={{ color: '#60a5fa' }}>Summary:</strong> {report.interviewSummary}
                        </Typography>

                        {report.candidateRating && (
                          <Typography
                            variant="body2"
                            paragraph
                            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                          >
                            <strong style={{ color: '#60a5fa' }}>Rating:</strong> {report.candidateRating}
                          </Typography>
                        )}

                        <Typography
                          variant="body2"
                          paragraph
                          sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                        >
                          <strong style={{ color: '#60a5fa' }}>Next Steps:</strong> {report.nextSteps}
                        </Typography>

                        {report.additionalNotes && (
                          <Typography
                            variant="body2"
                            paragraph
                            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                          >
                            <strong style={{ color: '#60a5fa' }}>Additional Notes:</strong> {report.additionalNotes}
                          </Typography>
                        )}

                        {report.salaryExpectation && (
                          <Typography
                            variant="body2"
                            paragraph
                            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                          >
                            <strong style={{ color: '#60a5fa' }}>Salary Expectation:</strong> ${report.salaryExpectation.toLocaleString()}
                          </Typography>
                        )}

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            M4 Consultant: {report.m4Consultant}
                          </Typography>
                          {report.scheduledFollowup && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                            >
                              Follow-up: {report.scheduledFollowup.toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />
            </Box>
          ))}
        </Box>
      </TabPanel>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body {
            font-size: 12px;
          }
          .MuiCard-root {
            box-shadow: none !important;
            border: 1px solid #ddd;
            break-inside: avoid;
          }
        }
      `}</style>
    </Box>
  );
};

export default DailyReporting;